<template>
	<div>
		<v-form @submit.prevent="fetchInventory">
			<v-row dense class="mb-1">
				<!-- owners -->
				<v-col lg="3" md="3" sm="4" cols="6">
					<v-autocomplete
						id="autoCompleteMultiple"
						v-model="filterData.ownerIds"
						:items="[
							{id: '00000000-0000-0000-0000-000000000000', name: 'Ugarit', type: 1 },
							...owners
						]"
						item-text="name"
						item-value="id"
						:label="$T('inputs.owner')"
						multiple
						dense
						outlined
						hide-details
						clearable
					>
						<template v-slot:selection="{ index, item }">
							<span v-if="index === 0">{{ item.name }}</span>
							<span v-if="index === 1">  +({{ filterData.ownerIds.length - 1 }}) </span>
						</template>
					</v-autocomplete>
				</v-col>
				<!-- item -->
				<v-col lg="2" md="3" sm="4" cols="6">
					<v-autocomplete
						v-model="filterData.itemId"
						:label="$T('inputs.item')"
						:items="items.filter(c => c.type === type.sub && !c.isMix)"
						item-text="name"
						item-value="id"
						dense
						outlined
						hide-details
						clearable
					/>
				</v-col>

				<!-- date -->
				<v-col lg="2" md="3" sm="4" cols="6">
					<bee-date-picker
						v-model="filterData.date"
						menu-picker
						:input-label="$T('inputs.date')"
						:input-props="{ readonly: true,outlined: true, dense: true }"
						hide-details
					/>
				</v-col>

				<v-col cols="auto">
					<v-btn small type="submit" color="primary">
						<v-icon>$search</v-icon>
					</v-btn>
				</v-col>

				<v-spacer/>

				<!-- view-details -->
				<!-- <v-col cols="auto">
					<v-checkbox
						v-model="filterData.viewDetails"
						:label="$T('view-details')"
						hide-details
						class="ma-0"
						@change="fetchInventory"
					/>
				</v-col> -->	

				<v-col class="d-flex align-center">
					<v-btn
						min-width="30"
						width="40"
						height="30"
						outlined
						:ripple="false"
						color="primary"
						class="me-1"
						@click="generatePrint"
					><v-icon>$print</v-icon></v-btn>
					
					<v-menu
						v-model="showMenu"
						absolute
						offset-y
						style="max-width: auto"
						dense	
					>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								v-bind="attrs"
								v-on="on"
								dense
								height="30"
								class="px-1 black--text"
							>
								<div class="px-2 py-0 d-flex align-center">
									{{ $t('cardamom-in-the-detachment') }}
									<span class="text-h5 primary--text font-weight-bold mx-2">
										{{ 
											cardamomAmountInSorting.length 
												? $options.filters.numberFormat(fixNumber(cardamomAmountInSorting
													.map(c => c.remainingQty > 0 ? c.remainingQty : 0)
													.reduce((a, b) => a+ b)))
												: '-' 
										}}
									</span> 
									<v-icon> mdi-chevron-down </v-icon>
								</div>
							</v-btn>
						</template>

						<bee-handy-table
							:headers="headersCardamom"
							:items="cardamomAmountInSorting"
							zebra
							dense
							:items-per-page="-1"
							:data-table-props="{ disableSort: true }"
							hide-default-footer
						></bee-handy-table>
					</v-menu>
				</v-col>
			</v-row>
		</v-form>

		<bee-handy-table
			:headers="headers"
			:items="loading ? [] : inventory"
			fixed-header
			:height="$vuetify.breakpoint.height - 110"
			zebra
			dense
			:loading="loading"
			:items-per-page="-1"
			hide-default-footer
			:data-table-props="{ disableSort: true }"
		>
			<template v-slot:item.ownerId="{ item }">
				<span :class="{'orange--text text--darken-4': item.ownerId}">
					{{item.ownerId ? getAccountById(item.ownerId).name : 'Ugarit'}}
				</span>
			</template>

			<template v-slot:item.actions="{ item }">
				<v-tooltip top>
					<template v-slot:activator="{ attr, on }">
						<v-btn
							v-bind="attr"
							v-on="on"
							icon
							color="primary"
							:to="{ name: 'items-statement', query: { itemId: item.itemId } }"
						>
							<v-icon>mdi-file-arrow-left-right</v-icon>
						</v-btn>
					</template>
					{{$T('tooltips.item-statement')}}
				</v-tooltip>
			</template>
		</bee-handy-table>
	</div>
</template>

<script>
import { type, cardamomStatus } from '@/helpers/enums';
import { mapGetters, mapState } from 'vuex';
import { fixNumber } from '@/helpers/functions'
import { generateHtmlPdf } from '@/helpers/generate-html';
import printJs from 'print-js';
import moment from 'moment';

export default {
	name: 'Inventory',
	
	data() {
		return {
			type,

			loading: false,
			filterData: {
				itemId: null,
				date: null,
				viewDetails: false,
				ownerIds: ['00000000-0000-0000-0000-000000000000']
			},
			showMenu: false,
			cardamomStatus,
			ownerIds: []
		}
	},

	computed: {
		...mapState({
			items: state => state.items.items,
			inventory: state => state.inventory.inventory,
			cardamomAmountInSorting: state => state.transactions.cardammanAmount,
			owners: state => state.configs.configsTrees.owners
		}),

		...mapGetters({
			getAccountById: 'accounts/getAccountById',
			getItemById: 'items/getItemById',
		}),

		headers() {
			const headers = [
				{
					text: this.$T('headers.item'),
					name: 'itemId',
					value: 'itemId',
					setValueExpr: val => this.getItemById(val)?.name
				},
				{
					text: this.$T('headers.lot'),
					name: 'lot',
					value: 'lot'
				},
				{
					text: this.$T('headers.owner'),
					name: 'ownerId',
					value: 'ownerId',
					printValueExpr: val =>
						val !== null
							? { style: 'color: #e65100', text: this.getAccountById(val)?.name || '-' }
							: 'Ugarit'
				},
				{
					text: this.$T('headers.remaining-qty'),
					name: 'remainingQty',
					value: 'remainingQty',
					type: 'number-format',
					printValueExpr: val => fixNumber(val || 0),
					noNotAvailableText: true
				},
				{
					text: '',
					name: 'actions',
					value: 'actions',
					width: 40,
					sortable: false,
					noPrint: true
				},
			];
			return headers;
		},

		headersCardamom() {
			return [
				{ 
					text: 'Owner', 
					name: 'ownerId', 
					value: 'ownerId', 
					noNotAvailableText: true,
					setValueExpr: val => val !== null ? this.getAccountById(val)?.name?.name : 'Ugarit'
				}, 
				{ 
					text: 'Quantity',
					name: 'remainingQty', 
					value: 'remainingQty', 
					setValueExpr: val => val + ' qq',
					type: 'number-format',
					columnClass: 'font-weight-bold' 
				}
			]
		},
	},

	methods: {
		fixNumber,
		fetchInventory() {
			this.loading = true;
			this.$store.dispatch('inventory/fetchAll', {
				materialId: this.filterData.itemId,
				date: this.filterData.date,
				withDetails: this.filterData.viewDetails,
				ownerIds: this.filterData.ownerIds
			}).finally(() => {
				this.loading = false;
			})
		},

		generatePrint() {
			const headers = this.headers.filter(c => !c.noPrint);
			const html = generateHtmlPdf({
				titleData: {
					Type: 'Inventory Report',
					Owners: this.filterData.ownerIds.map(c => this.getAccountById(c)?.name || 'Ugarit'),
					Date: moment().format('YYYY-MM-DD hh:mm a')
				},
				headers: headers.map(c => c.text),
				data: this.inventory.map(item =>
					headers.map(header => 
						header.printValueExpr
							? header.printValueExpr(item[header.value])
							: header.setValueExpr
								? header.setValueExpr(item[header.value])
								: item[header.value]
					)
				)
			});

			printJs({
				printable: html,
				type: 'raw-html',
			})
		},
	},

	created() {
		this.fetchInventory();	
		this.$store.dispatch('transactions/fetchCardammon', { 
			cardammomStatus: cardamomStatus.inSorting, 
			Withdetails: true
		})
	}
}
</script>

<style>
.v-input__slot {
	height: 34px !important;
}
</style>